import React, { useState } from "react";
import { Row, Col } from "reactstrap";
import { NeoModal, NeoButton, NeoBadge } from "../../../../../components/Common";
import { useTenant } from "../../../../../hooks/useTenant";
import { formatCurrency } from "../../../../../utils/format";
import { formatDate } from "../../../../../utils/date";

import { MEMBERSHIP_STATUS_STYLES, MEMBERSHIP_DURATION_LABELS } from "../../constants";

// Subcomponentes de Ações
import ActionAddDays from './MembershipActions/ActionAddDays';
import ActionSuspend from './MembershipActions/ActionSuspend';
import ActionEndSuspension from './MembershipActions/ActionEndSuspension';
import ActionCancel from './MembershipActions/ActionCancel';
import ActionTransfer from './MembershipActions/ActionTransfer';
import ActionAdjustCredits from './MembershipActions/ActionAdjustCredits';
import ActionUsageDetails from './MembershipActions/ActionUsageDetails';
import ActionChangePlan from './MembershipActions/ActionChangePlan';

/* ───────────────── COMPONENT ───────────────── */
const MembershipActionsModal = ({ isOpen, toggle, membership, onRefresh }) => {
    const { idTenant, user } = useTenant();
    const [activeAction, setActiveAction] = useState("addDays");

    if (!membership) return null;

    const status = MEMBERSHIP_STATUS_STYLES[membership.status] || MEMBERSHIP_STATUS_STYLES.expired;
    const isActive = membership.status === "active";
    const isSuspended = membership.status === "suspended";
    const isEditable = isActive || isSuspended;
    const durationText = (MEMBERSHIP_DURATION_LABELS[membership.durationType] || ((d) => `${d} período(s)`))(membership.duration);

    // Progresso
    let progressPercent = 0;
    let daysRemaining = 0;
    if (membership.startDate && membership.endDate && isActive) {
        const now = new Date();
        const s = new Date(membership.startDate + "T00:00:00");
        const e = new Date(membership.endDate + "T23:59:59");
        const total = e - s;
        const elapsed = now - s;
        progressPercent = total > 0 ? Math.min(100, Math.max(0, Math.round((elapsed / total) * 100))) : 0;
        daysRemaining = Math.max(0, Math.ceil((e - now) / (1000 * 60 * 60 * 24)));
    }

    const resetForm = () => {
        setActiveAction("addDays");
    };

    const handleClose = () => { resetForm(); toggle(); };

    const selectAction = (key) => {
        if (activeAction === key) return setActiveAction(null);
        setActiveAction(key);
    };

    /* ── InfoField helper ── */
    const InfoField = ({ label, icon, children }) => (
        <div>
            <div className="text-muted font-size-11 text-uppercase mb-1" style={{ letterSpacing: "0.5px" }}>{label}</div>
            <div className="font-size-13 fw-medium text-dark">
                {icon && <i className={`mdi ${icon} me-1 font-size-14`} style={{ opacity: 0.6 }}></i>}
                {children}
            </div>
        </div>
    );

    /* ── ActionButton helper ── */
    const ActionBtn = ({ id, icon, label, disabled }) => {
        const selected = activeAction === id;
        return (
            <NeoButton
                variant={selected ? "primary" : "outline-primary"}
                size="sm"
                disabled={disabled}
                onClick={() => selectAction(id)}
                icon={icon}
                className="neo-filter-search-btn"
            >
                {label}
            </NeoButton>
        );
    };

    return (
        <NeoModal
            isOpen={isOpen}
            toggle={handleClose}
            title="Gerenciar Plano"
            subtitle={membership.memberName || "Aluno"}
            headerIcon="mdi-file-document-outline"
            size="lg"
            scrollable={true}
        >

                {/* ── Resumo ── */}
                <div 
                    className="border p-3 mb-4 shadow-sm bg-light" 
                    style={{ 
                        borderRadius: "12px",
                        borderColor: "rgba(0, 0, 0, 0.05)"
                    }}
                >

                    {/* Plano + Status */}
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <h6 className="fw-bold text-dark mb-1 font-size-15">
                                {membership.membershipName || "Plano sem nome"}
                            </h6>
                            <span className="text-muted font-size-12">
                                <i className="mdi mdi-timer-sand me-1"></i>{durationText}
                            </span>
                            {membership.isScholarship && (
                                <NeoBadge color="info" className="ms-2 font-size-10 px-2" icon="mdi-school">
                                    Bolsista
                                </NeoBadge>
                            )}
                        </div>
                        <NeoBadge color={status.color} icon={status.icon} className="font-size-12 px-3 py-1">
                            {status.label}
                        </NeoBadge>
                    </div>

                    {/* Grid de Informações — 4 colunas */}
                    <Row className="g-3 mb-3">
                        <Col xs={6} md={3}>
                            <InfoField label="Início" icon="mdi-calendar-start">
                                {formatDate(membership.startDate)}
                            </InfoField>
                        </Col>
                        <Col xs={6} md={3}>
                            <InfoField label="Término" icon="mdi-calendar-end">
                                {formatDate(membership.endDate)}
                            </InfoField>
                        </Col>
                        <Col xs={6} md={3}>
                            <InfoField label="Valor">
                                <span className="fw-bold">{formatCurrency(membership.price)}</span>
                                {membership.discount > 0 && (
                                    <span className="text-muted font-size-11 text-decoration-line-through ms-1">
                                        {formatCurrency(membership.originalValue)}
                                    </span>
                                )}
                            </InfoField>
                        </Col>
                        <Col xs={6} md={3}>
                            <InfoField label="Restante">
                                {membership.durationType === 'classes' ? (
                                    <span className={`fw-bold ${(membership.remainingClasses || 0) <= 2 ? 'text-danger' : ''}`}>
                                        <i className="mdi mdi-ticket-outline me-1"></i>
                                        {membership.remainingClasses || 0}/{membership.totalClasses || 0} aulas
                                    </span>
                                ) : isActive ? (
                                    <span className={daysRemaining <= 7 ? "text-danger fw-bold" : "fw-bold"}>
                                        {daysRemaining} {daysRemaining === 1 ? "dia" : "dias"}
                                    </span>
                                ) : "—"}
                            </InfoField>
                        </Col>
                    </Row>

                    {/* Progresso */}
                    {isActive && (
                        <div>
                            {membership.durationType === 'classes' ? (
                                <>
                                    <div className="d-flex justify-content-between font-size-11 text-muted mb-1">
                                        <span>Aulas consumidas</span>
                                        <span className="fw-bold">{(membership.totalClasses || 0) - (membership.remainingClasses || 0)} de {membership.totalClasses || 0}</span>
                                    </div>
                                    <div className="progress rounded-pill" style={{ height: 6, backgroundColor: "rgba(72, 39, 18, 0.08)" }}>
                                        <div
                                            className={`progress-bar ${(membership.remainingClasses || 0) <= 2 ? "bg-danger" : "bg-success"}`}
                                            style={{ width: `${membership.totalClasses ? Math.round(((membership.totalClasses - (membership.remainingClasses || 0)) / membership.totalClasses) * 100) : 0}%`, transition: "width 0.4s", borderRadius: '10px' }}
                                        ></div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="d-flex justify-content-between font-size-11 text-muted mb-1">
                                        <span>Progresso da vigência</span>
                                        <span className="fw-bold">{progressPercent}%</span>
                                    </div>
                                    <div className="progress rounded-pill" style={{ height: 6, backgroundColor: "rgba(72, 39, 18, 0.08)" }}>
                                        <div
                                            className={`progress-bar ${progressPercent > 80 ? "bg-warning" : "bg-primary"}`}
                                            style={{ width: `${progressPercent}%`, transition: "width 0.4s", borderRadius: '10px' }}
                                        ></div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Suspensão info */}
                    {membership.allowsSuspension && (
                        <div className="mt-3 pt-2 border-top d-flex justify-content-between font-size-12">
                            <span className="text-muted">
                                <i className="mdi mdi-snowflake me-1"></i>Suspensão disponível
                            </span>
                            <span className="fw-medium">
                                {membership.disponibleSuspensionDays || 0} / {membership.maxSuspensionDays || 0} dias
                            </span>
                        </div>
                    )}

                    {/* Cancelamento info */}
                    {membership.cancelDate && (
                        <div className="mt-3 pt-2 border-top">
                            <div className="d-flex justify-content-between font-size-12">
                                <span className="text-danger"><i className="mdi mdi-close-circle me-1"></i>Cancelado em</span>
                                <span className="text-danger fw-medium">{formatDate(membership.cancelDate)}</span>
                            </div>
                            {membership.cancelReason && (
                                <div className="font-size-11 text-muted mt-1">Motivo: {membership.cancelReason}</div>
                            )}
                        </div>
                    )}

                    {/* Suspensão Ativa info */}
                    {isSuspended && membership.freezes?.length > 0 && (
                        (() => {
                            const lastSuspension = membership.freezes[membership.freezes.length - 1];
                            const suspEnd = new Date(lastSuspension.startDate + "T00:00:00");
                            suspEnd.setDate(suspEnd.getDate() + lastSuspension.days);
                            const now = new Date();
                            const diffDays = Math.ceil((suspEnd - now) / (1000 * 60 * 60 * 24));
                            const remaining = Math.max(0, diffDays);

                            return (
                                <div className="mt-3 pt-2 border-top">
                                    <div className="d-flex justify-content-between font-size-12 mb-2">
                                        <span className="text-warning fw-bold"><i className="mdi mdi-pause-circle me-1"></i>Plano Suspenso Atualmente</span>
                                    </div>
                                    <div className="font-size-12 d-flex flex-wrap gap-3">
                                        <span className="text-muted">Início: <strong className="text-dark">{formatDate(lastSuspension.startDate)}</strong></span>
                                        <span className="text-muted">Duração agendada: <strong className="text-dark">{lastSuspension.days} dias</strong></span>
                                        <span className={remaining <= 3 ? "text-danger fw-bold" : "text-muted fw-bold"}>
                                            Restam: {remaining} dias
                                        </span>
                                    </div>
                                    {lastSuspension.reason && (
                                        <div className="font-size-11 text-muted mt-1">Motivo: {lastSuspension.reason}</div>
                                    )}
                                </div>
                            );
                        })()
                    )}

                </div>

                {/* ── Botões de Ação ── */}
                {isEditable && (
                    <>
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <span className="text-muted font-size-12 text-uppercase fw-bold" style={{ letterSpacing: "0.5px" }}>
                                <i className="mdi mdi-cog-outline me-1"></i>Ações disponíveis
                            </span>
                            <div style={{ flex: 1, height: 1, backgroundColor: "#e9ecef" }}></div>
                        </div>

                        <div className="d-flex flex-wrap gap-2 mb-3">
                            <ActionBtn id="addDays" icon="mdi-calendar-plus" label="Dias +" disabled={!isActive} />
                            <ActionBtn id="removeDays" icon="mdi-calendar-minus" label="Dias −" disabled={!isActive} />
                            <ActionBtn id="transfer" icon="mdi-swap-horizontal-bold" label="Transferir" disabled={!isActive} />
                            
                            {!isSuspended && (
                                <ActionBtn id="suspend" icon="mdi-pause-circle-outline" label="Suspender Plano"
                                    disabled={!isActive || !membership.allowsSuspension} />
                            )}
                            
                            {isSuspended && (
                                <ActionBtn id="endSuspend" icon="mdi-play-circle-outline" label="Finalizar Suspensão" />
                            )}
                            
                            {membership.durationType === 'classes' && (
                                <>
                                    <ActionBtn id="viewUsage" icon="mdi-history" label="Ver Consumo" disabled={false} />
                                    <ActionBtn id="adjustCredits" icon="mdi-ticket-percent-outline" label="Ajustar Aulas" disabled={!isActive} />
                                </>
                            )}
                            
                            <ActionBtn id="changePlan" icon="mdi-swap-horizontal-bold" label="Trocar Plano" disabled={!isActive} />
                            <ActionBtn id="cancel" icon="mdi-close-circle-outline" label="Cancelar" disabled={false} />
                        </div>
                    </>
                )}

                {/* ── Painel expandido da ação ── */}
                {activeAction && (
                    <div className="mt-3" style={{ minHeight: '240px' }}>
                        {(activeAction === "addDays" || activeAction === "removeDays") && (
                            <ActionAddDays
                                activeAction={activeAction}
                                membership={membership} idTenant={idTenant} user={user} onRefresh={onRefresh}
                            />
                        )}

                        {activeAction === "transfer" && (
                            <ActionTransfer
                                membership={membership} idTenant={idTenant} user={user} onRefresh={onRefresh}
                            />
                        )}

                        {activeAction === "suspend" && (
                            <ActionSuspend
                                membership={membership} idTenant={idTenant} user={user} onRefresh={onRefresh}
                            />
                        )}

                        {activeAction === "endSuspend" && (
                            <ActionEndSuspension
                                membership={membership} idTenant={idTenant} user={user} onRefresh={onRefresh}
                            />
                        )}

                        {activeAction === "cancel" && (
                            <ActionCancel
                                membership={membership} idTenant={idTenant} user={user} onRefresh={onRefresh}
                            />
                        )}

                        {activeAction === "adjustCredits" && membership.durationType === 'classes' && (
                            <ActionAdjustCredits
                                membership={membership} idTenant={idTenant} user={user} onRefresh={onRefresh}
                            />
                        )}

                        {activeAction === "viewUsage" && membership.durationType === 'classes' && (
                            <ActionUsageDetails
                                membership={membership} idTenant={idTenant} user={user}
                            />
                        )}

                        {activeAction === "changePlan" && (
                            <ActionChangePlan
                                membership={membership} idTenant={idTenant} user={user} onRefresh={onRefresh}
                            />
                        )}
                    </div>
                )}

                {/* Nota: plano inativo */}
                {!isEditable && (
                    <div 
                        className="p-3 text-center rounded-3 bg-light border-0 shadow-sm"
                    >
                        <span className="text-muted font-size-12" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <i className="mdi mdi-information-outline me-1"></i>
                            Este plano está <strong>{status.label.toLowerCase()}</strong> — as ações foram desabilitadas.
                        </span>
                    </div>
                )}
            </NeoModal>
    );
};

export default MembershipActionsModal;
